import { useEffect, useRef } from 'react';
import { API } from '../../../../../api';

const TICK_MS = 30_000;
const IDLE_THRESHOLD_MS = 5 * 60_000;
/** The BE clamps a single ping at 120s; don't bother sending more than that. */
const MAX_SEGMENT_MS = 120_000;
const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll'
];

/**
 * Mounted trackers keyed by grpId. The "Create" button that ends the creation
 * wizard lives in a child route while the heartbeat is mounted on the flow's
 * layout (ProgramCreationFlow), so `finalizeAimdBuildTime` needs a way to reach
 * the live tracker and flush the seconds the 30s tick hasn't sent yet.
 */
const pendingSecondsByGroup = new Map<string, () => number>();

/**
 * Sends a heartbeat to the BE every 30s while the admin is actively working
 * inside the AIMD flow. Pauses when the tab is hidden or the user has been
 * idle for more than 5 minutes.
 *
 * Each ping carries the seconds actually elapsed since the last accounted
 * moment (not a flat 30s), so the tail before a "Create" click can be flushed
 * exactly — see `finalizeAimdBuildTime`.
 */
export function useAimdHeartbeat(grpId: string | undefined | null) {
  const lastActivityRef = useRef<number>(Date.now());
  const lastAccountedRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!grpId) return;

    lastActivityRef.current = Date.now();
    lastAccountedRef.current = Date.now();

    const bumpActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Consumes the elapsed active time since the last accounted moment and
    // returns it in whole seconds. Always moves the accounting cursor forward,
    // so time spent hidden/idle is dropped rather than credited later.
    const takePendingSeconds = (): number => {
      const now = Date.now();
      const elapsed = now - lastAccountedRef.current;
      lastAccountedRef.current = now;
      if (document.hidden) return 0;
      if (now - lastActivityRef.current > IDLE_THRESHOLD_MS) return 0;
      return Math.floor(Math.min(Math.max(0, elapsed), MAX_SEGMENT_MS) / 1000);
    };

    // A hidden tab throttles timers, so the tick after the admin comes back
    // would otherwise report the whole background period as active time.
    const onVisibilityChange = () => {
      lastAccountedRef.current = Date.now();
      if (!document.hidden) lastActivityRef.current = Date.now();
    };

    pendingSecondsByGroup.set(grpId, takePendingSeconds);
    ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, bumpActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibilityChange);

    const interval = setInterval(() => {
      const seconds = takePendingSeconds();
      if (seconds <= 0) return;
      (API as any).postAimdHeartbeat(grpId, seconds).catch(() => {});
    }, TICK_MS);

    return () => {
      clearInterval(interval);
      if (pendingSecondsByGroup.get(grpId) === takePendingSeconds) {
        pendingSecondsByGroup.delete(grpId);
      }
      ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, bumpActivity));
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [grpId]);
}

/**
 * ATC: flush the un-sent tail and freeze the program's build time on the BE.
 * Call it at the exact moment the admin hits "Create" at the end of the
 * creation wizard — that click is where the product defines creation as
 * finished, so the number Amplitude reports must stop there.
 *
 * Returns the frozen total in seconds (null when the call fails, or when a
 * previous "Create" already froze it and the BE couldn't report it back).
 */
export async function finalizeAimdBuildTime(grpId: string | undefined | null): Promise<number | null> {
  if (!grpId) return null;
  const takePendingSeconds = pendingSecondsByGroup.get(grpId);
  const pending = takePendingSeconds ? takePendingSeconds() : 0;
  try {
    const response = await (API as any).postAimdFinalize(grpId, pending);
    const seconds = response?.data?.seconds;
    return typeof seconds === 'number' ? seconds : null;
  } catch {
    return null;
  }
}
